function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useEffect, useMemo, useState, useRef } from 'react';
import compact from 'lodash/compact';
import { PageContentHeader, TableListProvider, TableListHeader, FilterPickerGroup, Searchbox, WithStatusIcon, useMediaQueries, MultiSelectFilterPicker, SingleSelectFilterPicker, Spinner, PaginationControl, useOverlayContext } from 'seagull';
import { useStateFromUrl } from 'seagull/utils/useStateFromUrl';
import { useIoContext, useStore, useStoreActions } from "../../contexts";
import { useSortFromUrl } from "../../hooks/useSortFromUrl";
import { useDebouncedLogSearchToBI } from "../../hooks/useDebouncedLogSearchToBI";
import { useOnMount } from "../../hooks/useOnMount";
import { selectors } from "../../store/state-selectors";
import { useFullPageInvoiceOnRefresh } from "../FullPageInvoice/FullPageInvoiceOpener";
import { NoFilteredInvoices } from "../InvoicesEmptyStates/InvoicesEmptyStates";
import { DateFilterPicker } from "./DateFilterPicker";
import { InvoiceHeaderItemID, applyOptimisticUpdate, getPaymentStatusFilterOptions, getInvoiceStatusFilterOptions, getQboStatusFilterOptions, mapSortAndFiltersRequestParams } from "./InvoicesTable.utils";
import InvoicesTableRow from "./InvoicesTableRow";
import { getURLWithFullPageInvoice } from "../FullPageInvoice/FullPageInvoice.utils";
import { invoiceModel } from "../../models";
import { useRestfulWrapper } from "../../hooks/useRestfulWrapper";
import { QboStatusFilter } from "../../business-logic/qboStatusFilterLogic";
import { generateUniqueId } from 'seagull/utils/generateUniqueId';
import { useBillingObservable } from "../../providers/BillingObservableProvider";
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import styles from "./InvoicesTable.module.scss";
import { handlePromise } from "../../infra/utils";
import { ErrorText } from "../../constants";
import { useAppConfig } from "../../store/store-context";
const defaultFilters = {
  date: 'all-time',
  status: [],
  paymentStatus: [],
  search: '',
  qbo: QboStatusFilter.All
};
export function InvoicesTableWithData(props) {
  const {
    filtersClassname,
    filters,
    columnDefs,
    headerItems,
    hideDueDate,
    sort,
    loading,
    pageNumber,
    isEmptyFilters,
    onSortChange,
    onFilterChange,
    onResetFilters,
    retry,
    onPageNumberChange
  } = props;
  const {
    statusFilterCounters,
    paymentStatusFilterCounters,
    qboStatusFilterCounters,
    total
  } = props.data;
  const {
    search,
    status: statusFilter,
    paymentStatus: paymentStatusFilter,
    date: dateFilter,
    qbo: qboStatusFilter
  } = filters;
  const {
    paginationPageSize
  } = useAppConfig();
  const [invoicesFromServer, setInvoicesFromServer] = useState(props.data.invoices);
  useEffect(() => setInvoicesFromServer(props.data.invoices), [props.data.invoices]);
  const {
    getNow
  } = useIoContext();
  const {
    subscribe,
    unsubscribe
  } = useBillingObservable();
  const isVendor = useStore(selectors.user.isVendor);
  const {
    logBI,
    fetchIntegrations
  } = useStoreActions();
  const showQboIntegration = useStore(selectors.integration.showQboIntegration);
  const {
    registerOnRefresh,
    unregisterOnRefresh
  } = useFullPageInvoiceOnRefresh();
  const debouncedLogSearchToBI = useDebouncedLogSearchToBI(11451);
  const invoiceStatusFilterOptions = getInvoiceStatusFilterOptions(statusFilterCounters);
  const paymentStatusFilterOptions = getPaymentStatusFilterOptions(isVendor, paymentStatusFilterCounters);
  const qboStatusFilterOptions = getQboStatusFilterOptions(qboStatusFilterCounters);
  useOnMount(() => {
    const id = generateUniqueId('observable-callback');
    subscribe({
      id,
      callback: () => retry()
    });
    return () => unsubscribe(id);
  });
  useOnMount(() => {
    if (showQboIntegration) {
      fetchIntegrations();
    }
    const retryId = 'retry';
    registerOnRefresh(retryId, async _ref => {
      let {
        optimisticUpdate
      } = _ref;
      if (optimisticUpdate) {
        setInvoicesFromServer(inv => applyOptimisticUpdate(inv, optimisticUpdate, getNow()));
        retry({
          disableFetchInvoices: true
        });
      } else {
        retry();
      }
    });
    return () => {
      unregisterOnRefresh(retryId);
    };
  });
  const handleSortChange = sort => {
    logBI(11450, {
      column: sort ? headerItems[sort.colIndex].id : 'none'
    });
    onSortChange(sort);
  };
  return /*#__PURE__*/React.createElement(TableListProvider, {
    hasActionColumn: true,
    columnDefs: columnDefs,
    sort: sort,
    onSortChange: handleSortChange
  }, /*#__PURE__*/React.createElement(PageContentHeader, null, /*#__PURE__*/React.createElement("div", {
    className: filtersClassname
  }, /*#__PURE__*/React.createElement(FilterPickerGroup, {
    "data-testid": "invoices-dashboard-filter-picker-group",
    onClearClick: onResetFilters,
    disableClear: isEmptyFilters
  }, /*#__PURE__*/React.createElement(Searchbox, {
    registerHotkey: true,
    value: search,
    "data-testid": "invoices-dashboard-search",
    onChange: event => {
      const search = event.target.value;
      onFilterChange({
        search
      }, {
        withDebounce: true
      });
      debouncedLogSearchToBI.onChange(search);
    },
    onClear: () => {
      onFilterChange({
        search: ''
      });
      debouncedLogSearchToBI.onClear();
    }
  }), /*#__PURE__*/React.createElement(MultiSelectFilterPicker, {
    "data-testid": "invoices-status-filter-picker",
    value: statusFilter,
    options: invoiceStatusFilterOptions,
    label: "Invoice status",
    onChange: status => {
      onFilterChange({
        status
      });
      if (status.length) logBI(11452, {
        status
      });
    }
  }), /*#__PURE__*/React.createElement(MultiSelectFilterPicker, {
    "data-testid": "invoices-payment-status-filter-picker",
    value: paymentStatusFilter,
    options: paymentStatusFilterOptions,
    label: "Payment status",
    onChange: paymentStatus => onFilterChange({
      paymentStatus
    })
  }), /*#__PURE__*/React.createElement(DateFilterPicker, {
    value: dateFilter,
    onChange: date => {
      onFilterChange({
        date
      });
      if (date !== defaultFilters.date) {
        logBI(11453, {
          date
        });
      }
    }
  }), showQboIntegration && qboStatusFilterOptions.length > 0 && /*#__PURE__*/React.createElement(SingleSelectFilterPicker, {
    "data-testid": "invoices-qbo-filter",
    label: "Sync status",
    value: qboStatusFilter,
    options: qboStatusFilterOptions,
    onChange: option => option && onFilterChange({
      qbo: option.value
    })
  }))), /*#__PURE__*/React.createElement(TableListHeader, {
    "data-testid": "invoices-table-header",
    items: headerItems
  })), loading ? /*#__PURE__*/React.createElement(Spinner, {
    centeredInBox: true,
    centerBoxHeight: 200
  }) : invoicesFromServer.length > 0 ? /*#__PURE__*/React.createElement(React.Fragment, null, invoicesFromServer.map((invoice, index) => /*#__PURE__*/React.createElement(InvoicesTableRow, {
    key: invoice.id,
    invoice: invoice,
    index: index,
    hideDueDate: hideDueDate,
    showQboIntegration: showQboIntegration,
    to: getURLWithFullPageInvoice({
      invoiceId: invoice.id,
      relationshipId: invoiceModel.relationshipId(invoice)
    }),
    retry: retry
  })), /*#__PURE__*/React.createElement("div", {
    className: styles.paginationControls
  }, /*#__PURE__*/React.createElement(PaginationControl, {
    pageSize: paginationPageSize,
    pageNumber: pageNumber,
    total: total,
    onPageNumberChange: onPageNumberChange
  }))) : /*#__PURE__*/React.createElement(NoFilteredInvoices, {
    resetFilters: onResetFilters
  }));
}
export default function InvoicesTable(props) {
  const {
    relationshipId,
    filtersClassname,
    onRetry,
    renderLoader,
    renderError,
    renderChild: _renderChild,
    renderNoInvoices
  } = props;
  const {
    getNow,
    downloadCsv
  } = useIoContext();
  const {
    paginationPageSize,
    inputDebounceTime,
    exportCsvFilename
  } = useAppConfig();
  const [pageNumber, _setPageNumber] = useState(1);
  const showQboIntegration = useStore(selectors.integration.showQboIntegration);
  const isPendingQboIntegrationSetup = useStore(selectors.integration.isPendingQboIntegrationSetup);
  const {
    smallScreen
  } = useMediaQueries();
  const hideDueDate = smallScreen;
  const {
    api
  } = useStoreActions();
  const {
    showErrorToast
  } = useOverlayContext();
  const [exportingCsv, setExportingCsv] = useState(false);
  const defaultSort = useMemo(() => ({
    colIndex: 0,
    sort: 'desc'
  }), []);
  const {
    columnDefs,
    headerItems,
    sortIds
  } = useMemo(() => {
    const invoiceID = 'Invoice ID';
    const tableDefs = compact([{
      headerItem: {
        id: InvoiceHeaderItemID.invoiceId,
        value: showQboIntegration && isPendingQboIntegrationSetup ? /*#__PURE__*/React.createElement(WithStatusIcon, {
          "data-testid": "missing-account-mapping-error",
          info: "Please finish setting up QBO by selecting the accounts in the integration setup page"
        }, invoiceID) : invoiceID
      },
      columnDef: {
        width: showQboIntegration ? 130 : 100,
        sortable: true
      }
    }, {
      headerItem: {
        id: InvoiceHeaderItemID.companyName,
        value: 'Company name'
      },
      columnDef: {
        width: [180, 400],
        sortable: true
      }
    }, {
      headerItem: {
        id: InvoiceHeaderItemID.status,
        value: 'Invoice status'
      },
      columnDef: {
        width: 96,
        sortable: true
      }
    }, {
      headerItem: {
        id: InvoiceHeaderItemID.additionalInformation,
        value: 'Payment status'
      },
      columnDef: {
        width: 220,
        sortable: true
      }
    }, {
      headerItem: {
        id: InvoiceHeaderItemID.issueDate,
        value: 'Issue Date'
      },
      columnDef: {
        width: [90, 100],
        sortable: true
      }
    }, !hideDueDate && {
      headerItem: {
        id: InvoiceHeaderItemID.dueDate,
        value: 'Due date'
      },
      columnDef: {
        width: [90, 100],
        sortable: true
      }
    }, {
      headerItem: {
        id: InvoiceHeaderItemID.total,
        value: 'Total'
      },
      columnDef: {
        width: 84,
        sortable: true,
        hAlign: 'end'
      }
    }]);
    const headerItems = tableDefs.map(d => d.headerItem);
    const columnDefs = tableDefs.map(d => d.columnDef);
    const sortIds = headerItems.map(item => item.id);
    return {
      columnDefs,
      headerItems,
      sortIds
    };
  }, [showQboIntegration, isPendingQboIntegrationSetup, hideDueDate]);
  const {
    sort,
    changeSort: _changeSort
  } = useSortFromUrl({
    defaultSort,
    sortIds
  });
  const {
    urlState,
    changeUrlState: _changeUrlState
  } = useStateFromUrl({
    status: {
      multi: true,
      defaultValue: defaultFilters.status
    },
    paymentStatus: {
      multi: true,
      defaultValue: defaultFilters.paymentStatus
    },
    date: {
      defaultValue: defaultFilters.date
    },
    search: {
      defaultValue: defaultFilters.search
    },
    qbo: {
      defaultValue: defaultFilters.qbo
    }
  });
  const urlStateRef = useRef(urlState);
  const sortRef = useRef(sort);
  const pageNumberRef = useRef(pageNumber);
  const {
    renderRestfulState,
    refresh
  } = useRestfulWrapper({
    fetch: () => api.invoice.getSummariesScout(_objectSpread(_objectSpread({}, mapSortAndFiltersRequestParams(_objectSpread(_objectSpread({}, urlStateRef.current), {}, {
      sort: sortRef.current,
      now: getNow(),
      sortIds
    }))), {}, {
      relationshipId,
      pageSize: paginationPageSize,
      pageNumber: pageNumberRef.current
    }))
  });
  useOnMount(() => {
    return () => refreshWithDebounce.cancel();
  });
  const refreshWithDebounce = useMemo(() => {
    const debounceTime = inputDebounceTime != null ? inputDebounceTime : 500;
    return debounce(refresh, debounceTime);
  }, []);
  function changeUrlState(newUrlState) {
    _changeUrlState(newUrlState);
    urlStateRef.current = _objectSpread(_objectSpread({}, urlStateRef.current), newUrlState);
  }
  function changeSort(newSort) {
    _changeSort(newSort);
    sortRef.current = newSort;
  }
  function setPageNumber(pageNumber) {
    _setPageNumber(pageNumber);
    pageNumberRef.current = pageNumber;
  }
  function handleFilterChange(newUrlState, opts) {
    changeUrlState(newUrlState);
    setPageNumber(1);
    scheduleRefresh(opts);
  }
  function handleSortChange(sort) {
    changeSort(sort);
    setPageNumber(1);
    scheduleRefresh();
  }
  function handlePageNumberChange(pageNumber) {
    setPageNumber(pageNumber);
    scheduleRefresh();
  }
  function scheduleRefresh() {
    let {
      withDebounce
    } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    refreshWithDebounce();
    !withDebounce && refreshWithDebounce.flush();
  }
  return renderRestfulState({
    renderLoader,
    renderError,
    renderChild: _ref2 => {
      let {
        data,
        loading,
        refresh
      } = _ref2;
      const isEmptyFilters = isEqual(urlState, defaultFilters);
      if (!data.invoices.length && !loading && renderNoInvoices && isEmptyFilters) {
        return renderNoInvoices();
      }
      const tableContent = /*#__PURE__*/React.createElement(InvoicesTableWithData, {
        data: data,
        columnDefs: columnDefs,
        headerItems: headerItems,
        hideDueDate: hideDueDate,
        sort: sort,
        filtersClassname: filtersClassname,
        filters: urlState,
        isEmptyFilters: isEmptyFilters,
        loading: loading,
        pageNumber: pageNumber,
        onSortChange: handleSortChange,
        onFilterChange: handleFilterChange,
        onResetFilters: () => handleFilterChange(defaultFilters),
        onPageNumberChange: handlePageNumberChange,
        retry: async function retry() {
          let {
            disableFetchInvoices
          } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
          await Promise.all(compact([!disableFetchInvoices && refresh(), onRetry && onRetry()]));
        }
      });
      return _renderChild ? _renderChild({
        data,
        loading,
        tableContent,
        exportingCsv,
        refresh,
        getRows: () => data.invoices,
        async exportCsv() {
          setExportingCsv(true);
          const params = mapSortAndFiltersRequestParams(_objectSpread(_objectSpread({}, urlStateRef.current), {}, {
            sort: sortRef.current,
            sortIds,
            now: getNow()
          }));
          const result = await handlePromise(api.invoice.exportToCsv(params));
          if (result.success) {
            const filename = exportCsvFilename || 'exported-invoices.csv';
            downloadCsv(result.data, filename);
          } else {
            showErrorToast(ErrorText.UnexpectedErrorTryAgain, 'export-invoices-error');
          }
          setExportingCsv(false);
        }
      }) : tableContent;
    }
  });
}